import useDraftBulkDelete from "hooks/useDraftBulkDelete";
import DraftBulkActions, { DraftCheckbox } from "components/DraftBulkActions";
/* eslint-disable react-hooks/exhaustive-deps */
import { ClearableSearchInput } from "components/SearchField/SearchField";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  LuCheck,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuCopy,
  LuDownload,
  LuEllipsis,
  LuEye,
  LuFileText,
  LuMail,
  LuLandmark,
  LuMenu,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import { useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import ListingActionMenu from "components/ListingActionMenu";
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import EmailTemplateModal from "components/EmailModal/EmailTemplateModal";
import SendInvoiceModal from "./SendInvoiceModal";
import Skeleton from "components/Skeleton/Skeleton";
import CardSkeleton from "components/Skeleton/CardSkeleton";
import PageSkeleton from "components/Skeleton/PageSkeleton";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import { appConstants } from "utils/appConstants";
import { clearBillingNumberPreview, getBillingNumberPreview } from "utils/billingNumberPreview";
import { AllKeysHaveValue, getScreenRight, showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import { ResendEmail } from "utils/invoiceAPI";
import { getEmailAndTemplateForQuotation } from "utils/quotationAPI";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { getInvoiceStatusKey } from "utils/invoiceStatus";
import { matchesDueDateRange } from "utils/paymentDueDate";
import { documentDateFilterLabel } from "utils/documentDateFilterLabel";
import { downloadInvoice } from "utils/api";
import { savePdfResponse } from "utils/documentPdf";
import { toast } from "react-toastify";
import CreateInvoiceModal from "./CreateInvoiceModal";
import "./invoices.css";

const SIZE = 10;
const num = (v) => Number(String(v ?? 0).replace(/[^0-9.-]/g, "")) || 0;
const money = (v) => `$${formatCurrencyAUD(num(v))}`;
const date = (v, fallback = "—") => {
  if (!v) return fallback;
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};
const statusLabel = (k) =>
  ({
    overdue: "Overdue",
    awaiting: "Awaiting payment",
    paid: "Paid",
    voided: "Voided",
    draft: "Draft",
  }[k]);
const getInvoiceOpenPath = (row) =>
  `/invoices/${
    (row._key || getInvoiceStatusKey(row)) === "draft" ? "edit" : "view"
  }/${row._id}`;
const invoiceActivityLabel = (row) => {
  const status = String(row.activityStatus || "").trim();
  if (status) return status;
  return Number(row.emailSent) > 0 ? "Sent" : "Draft";
};
const paymentStatusLabel = (row) => {
  if (row._key === "draft") return "Not issued";
  if (row.registerStatus === "paid") return "Paid";
  const status = String(
    row.invoiceStatus || row.paymentStatusLabel || ""
  ).trim();
  return status || statusLabel(row._key);
};
const statusClassName = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
const getContactOpenPath = (row) => {
  const contact = row?.contactID ?? row?.customerID ?? row?.contact;
  const contactId = typeof contact === "object" ? contact?._id : contact;
  return contactId ? `/contacts/${contactId}` : null;
};
const isInteractiveRowTarget = (target) =>
  Boolean(
    target?.closest?.(
      "a, button, input, select, textarea, [role='button'], [role='menu'], [role='menuitem'], .MuiButtonBase-root, .invoice-row-menu"
    )
  );

function ThemeSelect({ value, onChange, options, label }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className={`invoice-theme-select${open ? " open" : ""}`} ref={root}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{value}</span>
        <LuChevronDown />
      </button>
      {open && (
        <div className="invoice-theme-options" role="listbox">
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option === value}
              className={option === value ? "selected" : ""}
              key={option}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              <span>{option}</span>
              {option === value && <LuCheck />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
export default function InvoicesSearch() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStatus = searchParams.get("invoiceStatus");
  const routeTab =
    requestedStatus === "Draft"
      ? "draft"
      : requestedStatus === "Overdue"
      ? "overdue"
      : null;
  const currency = useSelector((s) => s.currency.currencySymbol) || "AUD";
  const login = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
    } catch {
      return {};
    }
  }, []);
  useEffect(() => {
    if (login?.businessUserID) {
      clearBillingNumberPreview("invoice", login.businessUserID);
      getBillingNumberPreview("invoice", login.businessUserID).catch(() => {});
    }
  }, [login?.businessUserID]);
  const organisation =
    login.companyName ||
    login.businessName ||
    login.organisationName ||
    login.organizationName ||
    login.fullName ||
    login.name ||
    "My Organisation";
  const role = login.roleName || login.role || "Owner";
  const userName =
    `${login.firstName || ""} ${login.lastName || ""}`.trim() ||
    login.name ||
    "User";
  const [rows, setRows] = useState([]),
    [summary, setSummary] = useState(null),
    [total, setTotal] = useState(undefined),
    [loading, setLoading] = useState(false),
    [filterLoading, setFilterLoading] = useState(false),
    [access, setAccess] = useState(false),
    [nav, setNav] = useState(false);
  const requestedTab = new URLSearchParams(window.location.search).get("tab");
  const initialTab = [
    "all",
    "draft",
    "awaiting",
    "overdue",
    "paid",
    "voided",
  ].includes(requestedTab)
    ? requestedTab
    : "all";
  const [tab, setTab] = useState(initialTab),
    [search, setSearch] = useState(""),
    [customer, setCustomer] = useState("All customers"),
    [range, setRange] = useState("All dates"),
    [page, setPage] = useState(1),
    [menu, setMenu] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  useEffect(() => {
    if (routeTab) {
      setTab(routeTab);
      setPage(1);
    }
  }, [routeTab]);
  const [deleteOpen, setDeleteOpen] = useState(false),
    [deleteId, setDeleteId] = useState(""),
    [deleteNumber, setDeleteNumber] = useState(""),
    [deleteLoading, setDeleteLoading] = useState(false),
    [deleteError, setDeleteError] = useState(""),
    [emailOpen, setEmailOpen] = useState(false),
    [selectedRowForEmail, setSelectedRowForEmail] = useState(null);
  const [editBlockedRow, setEditBlockedRow] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [bankingGateOpen, setBankingGateOpen] = useState(false);
  const [checkingBanking, setCheckingBanking] = useState(false);
  const hasBankingDetails = (profile) =>
    [profile?.bankAccountName, profile?.bankBsbCode, profile?.bankAccountNumber]
      .every((value) => String(value || "").trim());
  const handleOpenNewInvoice = async () => {
    if (!access) return showResMsg(genericMessage.notPermission, 2000, false);
    if (checkingBanking) return;
    setCheckingBanking(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}user/fetchUserProfile`,
        { _id: login._id },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` } }
      );
      const profile = response.data?.Result_Output?.[0];
      if (hasBankingDetails(profile)) setCreateInvoiceModalOpen(true);
      else setBankingGateOpen(true);
    } catch (_) {
      setBankingGateOpen(true);
    } finally {
      setCheckingBanking(false);
    }
  };
  const [template, setTemplate] = useState({
    screenType: "Invoices",
    userEmailAddress: "",
    customerEmailAddress: [],
    emailTemplate: "",
  });
  const filterLoadingTimer = useRef(null);
  const beginFilterLoading = () => {
    window.clearTimeout(filterLoadingTimer.current);
    setFilterLoading(true);
    filterLoadingTimer.current = window.setTimeout(
      () => setFilterLoading(false),
      250
    );
  };
  const summaryDay = new Date();
  const request = {
    _id: [],
    businessUserID: login.businessUserID,
    sortingType: "",
    sortingField: "",
    invoiceStatus: "All",
    activityStatus: "All",
    durationDate: "",
    fromDate: "",
    toDate: "",
    page: 1,
    limit: 100,
    filterValues: "",
    sendEmailFlag: false,
    includeSummary: true,
    registerView: true,
    allRegisterRows: true,
    summaryDate: `${summaryDay.getFullYear()}-${String(
      summaryDay.getMonth() + 1
    ).padStart(2, "0")}-${String(summaryDay.getDate()).padStart(2, "0")}`,
    searchParameterList: [
      "invoiceStatus",
      "activityStatus",
      "invoiceDate",
      "dueDate",
      "customerName",
      "invoiceNumber",
      "quotationNumber",
      "customerCompanyName",
      "purchaseOrderNumber",
    ],
  };
  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}invoice/fetchInvoices`,
        request,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      );
      const output = response.data?.Result_Output || {};
      if (
        response.data?.Result_Status !== true ||
        !Array.isArray(output.docs) ||
        !output.summary ||
        output.docs.length !== output.totalDocs
      ) {
        throw new Error("The complete invoice list could not be loaded.");
      }
      setRows(output.docs);
      setSummary(output.summary);
      setTotal(output.totalDocs);
    } catch (e) {
      console.error(e);
      showResMsg("Unable to load invoices.", 2000, false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    sessionStorage.removeItem("HoldQuotationData");
    sessionStorage.removeItem("deletedItems");
    getScreenRight("Invoices").then((r) => setAccess(r === "Full Access"));
    fetchRows();
  }, []);
  useEffect(() => () => window.clearTimeout(filterLoadingTimer.current), []);
  const data = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        _key: getInvoiceStatusKey(r),
        _activityStatus: invoiceActivityLabel(r),
        _customer: r.customerCompanyName || r.customerName || r.name || "—",
        _total: num(r.finalAmount ?? r.totalAmount),
        _balance:
          r.payablePendingAmount !== undefined
            ? num(r.payablePendingAmount)
            : num(r.paymentStatus),
      })),
    [rows]
  );
  const customers = useMemo(
    () => [
      "All customers",
      ...new Set(data.map((r) => r._customer).filter((x) => x !== "—")),
    ],
    [data]
  );
  const dateFilteredRows = useMemo(
    () => data.filter((row) =>
      matchesDueDateRange(row.dueDate, range) &&
      (range !== "Overdue" || row._key === "overdue")
    ),
    [data, range]
  );
  const dropdownFilteredRows = useMemo(
    () => dateFilteredRows.filter((row) =>
      customer === "All customers" || row._customer === customer
    ),
    [dateFilteredRows, customer]
  );
  const matchingRows = useMemo(
    () =>
      dropdownFilteredRows.filter((r) => {
        return (
          (!search.trim() ||
            [
              r.invoiceNumber,
              r._customer,
              r.purchaseOrderNumber,
              r.quotationNumber,
            ].some((x) =>
              String(x || "")
                .toLowerCase()
                .includes(search.toLowerCase())
            ))
        );
      }),
    [dropdownFilteredRows, search]
  );
  const counts = useMemo(
    () =>
      matchingRows.reduce((result, row) => {
        result[row._key] = (result[row._key] || 0) + 1;
        return result;
      }, {}),
    [matchingRows]
  );
  const filtered = useMemo(
    () => matchingRows.filter((row) => tab === "all" || row._key === tab),
    [matchingRows, tab]
  );
  const visible = filtered.slice((page - 1) * SIZE, page * SIZE),
    pages = Math.max(1, Math.ceil(filtered.length / SIZE));
  const showClearFilters = Boolean(search.trim()) || customer !== "All customers" || range !== "All dates";
  const summaryFiltersActive = customer !== "All customers" || range !== "All dates";
  const filtersActive =
    tab !== "all" ||
    Boolean(search.trim()) ||
    customer !== "All customers" ||
    range !== "All dates";
  const filteredSummary = useMemo(() => {
    const counts = {
      all: dropdownFilteredRows.length,
      draft: 0,
      outstanding: 0,
      overdue: 0,
    };
    const amountCents = { draft: 0, outstanding: 0, overdue: 0 };
    dropdownFilteredRows.forEach((row) => {
      if (row._key === "draft") {
        counts.draft += 1;
        amountCents.draft += Math.round(row._total * 100);
      }
      if (row._balance > 0) {
        counts.outstanding += 1;
        amountCents.outstanding += Math.round(row._balance * 100);
      }
      const overdueAmount = num(row.overdueAmount);
      if (overdueAmount > 0) {
        counts.overdue += 1;
        amountCents.overdue += Math.round(overdueAmount * 100);
      }
    });
    return {
      counts,
      amounts: Object.fromEntries(
        Object.entries(amountCents).map(([key, value]) => [key, value / 100])
      ),
    };
  }, [dropdownFilteredRows]);
  const displayedSummary = summaryFiltersActive ? filteredSummary : summary;
  const listLoading = loading || filterLoading;
  const draftBulk = useDraftBulkDelete({
    enabled: tab === "draft" && access,
    rows: visible.filter((row) => row._key === "draft" && !Number(row.emailSent)),
    scopeKey: JSON.stringify([tab, page, search, customer, range]),
    loading: listLoading, endpoint: "invoice/deleteInvoice", businessUserID: login.businessUserID,
    onDeleted: async (count) => {
      await fetchRows();
      if (page > 1 && visible.length === count) setPage((current) => current - 1);
    },
  });
  const sums = displayedSummary?.amounts || {
    draft: 0,
    outstanding: 0,
    overdue: 0,
  };
  const clearFilters = () => {
    beginFilterLoading();
    setTab("all");
    setSearch("");
    setCustomer("All customers");
    setRange("All dates");
    setPage(1);
  };
  const openEmail = async (row) => {
    setMenu(null);
    setSelectedRowForEmail(row);
    if (!row.finalAmount || num(row.finalAmount) <= 0)
      return showResMsg("No item added", 2000, false);
    if (AllKeysHaveValue(template) && template.selectedID === row._id)
      return setEmailOpen(true);
    await getEmailAndTemplateForQuotation(
      {
        screenType: "Invoices",
        contactID: row.contactID,
        selectedID: row._id,
        userID: String(sessionStorage.getItem("USER_ID") || "").replaceAll(
          '"',
          ""
        ),
      },
      (d) => {
        if (d.Result_Status) {
          setTemplate(d.Result_Output);
          setEmailOpen(true);
        } else showResMsg(d.Result_Message, 2000, false);
      },
      dispatch
    );
  };
  const sendEmail = async (d) =>
    new Promise((resolve) => {
      const payload = {
        selectedID: d.selectedID || selectedRowForEmail?._id,
        userEmailAddress: d.userEmailAddress,
        customerEmailAddress: [...d.customerEmailAddress],
        emailTemplate: { ...d.emailTemplate },
        sendMeCopy: d.sendMeCopy,
        businessUserID: login.businessUserID,
      };
      ResendEmail(
        payload,
        (r) => {
          if (r?.Result_Status) {
            showResMsg(
              r?.Result_Message || "Invoice sent successfully.",
              2000,
              true
            );
            fetchRows();
            resolve({ success: true, message: r?.Result_Message });
          } else {
            resolve({
              success: false,
              message: r?.Result_Message || "Failed to send invoice email.",
            });
          }
        },
        dispatch
      );
    });
  const clone = async (row) => {
    if (cloneLoading) return;
    setMenu(null);
    setCloneLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    try {
      const response = await axios.post(
        `${appConstants.baseURL}invoice/cloneInvoice`,
        {
          businessUserID: login.businessUserID,
          invoiceID: row._id,
          userID: login._id,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 60000,
        }
      );
      const data = response.data;
      const clonedId = data?.Result_Output?._id;
      if (!data?.Result_Status || !clonedId) {
        showResMsg(
          data?.Result_Message ||
            "Unable to clone invoice. Please check the invoice list before trying again.",
          2000,
          false
        );
        return;
      }
      clearBillingNumberPreview("invoice", login.businessUserID);
      navigate(`/invoices/edit/${clonedId}`);
    } catch (error) {
      showResMsg(
        error.code === "ECONNABORTED" || error.code === "ETIMEDOUT"
          ? "Cloning took too long. Check the invoice list before trying again."
          : error.response?.data?.Result_Message ||
            "Unable to clone invoice. Please check the invoice list before trying again.",
        2000,
        false
      );
    } finally {
      setCloneLoading(false);
    }
  };
  const remove = () => {
    if (!deleteId || deleteLoading) return;
    setDeleteLoading(true);
    setDeleteError("");
    axios
      .post(
        `${appConstants.baseURL}invoice/deleteInvoice`,
        {
          ...request,
          _id: deleteId,
          token: login.token,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
        }
      )
      .then((r) => {
        if (!r.data.Result_Status) {
          setDeleteError(
            r.data.Result_Message || "Unable to move this invoice to Voided."
          );
          return;
        }
        showResMsg(
          r.data.Result_Message || "Invoice moved to Voided.",
          1600,
          true
        );
        setDeleteOpen(false);
        fetchRows();
      })
      .catch((error) => {
        setDeleteError(
          error.response?.data?.Result_Message ||
            "Unable to move this invoice to Voided."
        );
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };
  const tabs = [
    ["all", "All"],
    ["draft", "Draft"],
    ["awaiting", "Awaiting payment"],
    ["overdue", "Overdue"],
    ["paid", "Paid"],
    ["voided", "Voided"],
  ];
  const openInvoiceDetails = (row, event) => {
    if (event?.target?.closest(".invoice-row-menu, [data-row-action]")) {
      return;
    }
    navigate(getInvoiceOpenPath(row));
  };
  const handleInvoiceRowKeyDown = (row, event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openInvoiceDetails(row, event);
    }
  };
  return (
    <div className={`invoice-register-shell${darkMode ? " dark" : ""}`}>
      <DashboardNavigation
        open={nav}
        onClose={() => setNav(false)}
        activePath="/invoices"
        organisation={organisation}
        role={role}
        invoiceBadge={total ?? null}
      />
      <div className="invoice-register-main">
        <AppHeader
          onOpenNav={() => setNav(true)}
          userName={userName}
          readOnlySearch
        />
        <main className="invoice-register-content">
          {cloneLoading ? (
            <PageSkeleton type="editor" variant="document" label="Cloning invoice" />
          ) : (
            <>
          <div className="invoice-compact-org">
            <button onClick={() => setNav(true)}>
              <LuMenu />
            </button>
            <span>invoice &amp; recover</span>
            <div>
              <i>
                {organisation
                  .split(" ")
                  .map((x) => x[0])
                  .slice(0, 2)
                  .join("")}
              </i>
              <strong>
                {organisation}
                <small>{role}</small>
              </strong>
              <LuChevronDown />
            </div>
          </div>
          <header className="invoice-page-header">
            <div>
              <h1>Invoices</h1>
              <p>
                Manage invoices, track payments, and follow up on outstanding
                balances.
              </p>
            </div>
            <button
              className="invoice-new-button"
              disabled={!access || checkingBanking}
              onClick={handleOpenNewInvoice}
            >
              <LuPlus />
              New invoice
            </button>
            <p className="invoice-scope">
              {organisation} · All invoices · {currency}
            </p>
            <p className="invoice-snapshot">
              {documentDateFilterLabel(range)}
            </p>
          </header>
          <section className="invoice-summary-grid">
            {listLoading ? (
              <CardSkeleton variant="stat" count={3} />
            ) : (
              <>
                <article>
                  <div>
                    <h2>Outstanding</h2>
                    <LuFileText />
                  </div>
                  <strong>
                    {displayedSummary ? money(sums.outstanding) : "—"}
                  </strong>
                  <p>
                    {displayedSummary?.counts.outstanding ?? "—"} issued unpaid
                    invoices
                  </p>
                </article>
                <article>
                  <div>
                    <h2>Overdue</h2>
                    <LuFileText />
                  </div>
                  <strong>
                    {displayedSummary ? money(sums.overdue) : "—"}
                  </strong>
                  <p>
                    {displayedSummary?.counts.overdue ?? "—"} overdue · Included
                    in outstanding
                  </p>
                </article>
                <article>
                  <div>
                    <h2>Drafts</h2>
                    <LuFileText />
                  </div>
                  <strong>{displayedSummary ? money(sums.draft) : "—"}</strong>
                  <p>
                    {displayedSummary?.counts.draft ?? "—"} Unsent value · Not
                    receivables
                  </p>
                </article>
              </>
            )}
          </section>
          <p className="invoice-summary-note">
            Overdue is included in Outstanding. Unsent drafts are excluded.
            {displayedSummary
              ? summaryFiltersActive
                ? ` Summary covers the ${displayedSummary.counts.all} invoices matching the customer and due-date filters.`
                : ` Summary covers all ${displayedSummary.counts.all} invoices.`
              : " Summary totals are unavailable. Refresh to try again."}
          </p>
          <section className="invoice-filter-area">
            <div className="draft-bulk-header">
            <div className="invoice-tabs">
              {tabs.map(([k, l]) => (
                <button
                  key={k}
                  className={tab === k ? "active" : ""}
                  onClick={() => {
                    beginFilterLoading();
                    setTab(k);
                    setPage(1);
                  }}
                >
                  {l} ({k === "all" ? matchingRows.length : counts[k] || 0})
                </button>
              ))}
            </div>
            <DraftBulkActions bulk={draftBulk} />
            </div>
            <div className="invoice-filter-row">
              <label>
                <LuSearch />
                <ClearableSearchInput
                  value={search}
                  onChange={(e) => {
                    beginFilterLoading();
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search invoice number, customer or reference"
                />
              </label>
              <ThemeSelect
                label="Customer filter"
                value={customer}
                options={customers}
                onChange={(value) => {
                  beginFilterLoading();
                  setCustomer(value);
                  setPage(1);
                }}
              />
              <ThemeSelect
                label="Due-date range filter"
                value={range === "All dates" ? "All due dates" : range}
                options={[
                  "All due dates",
                  "Due this week",
                  "Due this month",
                  "This quarter",
                ]}
                onChange={(value) => {
                  beginFilterLoading();
                  setRange(
                    value === "All due dates" ? "All dates" : value
                  );
                  setPage(1);
                }}
              />
              {showClearFilters && (
                <button
                  type="button"
                  className="invoice-clear-filters"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
            <p>Date range uses due date; planned due date for unsent drafts.</p>
          </section>
          <section className="invoice-register-card">
            <div className="invoice-register-context">
              <strong>{filtered.length} invoices</strong>
              <span>Overdue first · Grouped by status</span>
            </div>
            <div className="invoice-table-head desktop">
              <span>Invoice number</span>
              <span>Customer</span>
              <span>Invoice date</span>
              <span>Due date</span>
              <span>Invoice status</span>
              <span>Payment status</span>
              <span>Invoice total</span>
              <span>Outstanding balance</span>
              <span>Actions</span>
            </div>
            <div className="invoice-table-head compact">
              <span>Customer / Invoice / Date</span>
              <span>Invoice / Payment / Due</span>
              <span>Total / Balance</span>
              <span>Actions</span>
            </div>
            <div className="invoice-table-body" aria-busy={listLoading}>
              {listLoading
                ? Array.from({ length: 8 }, (_, index) => (
                    <article
                      className="invoice-register-row invoice-register-row--loading"
                      key={`invoice-loading-${index}`}
                    >
                      {Array.from({ length: 9 }, (__, cell) => (
                        <span key={cell}>
                          <Skeleton
                            width={cell === 1 ? "78%" : cell === 8 ? 28 : "64%"}
                            height={cell === 4 || cell === 5 ? 22 : 14}
                            radius={cell === 4 || cell === 5 ? 999 : 6}
                          />
                        </span>
                      ))}
                    </article>
                  ))
                : visible.map((r, index) => {
                    const rowMenuKey = `${r._id}:${index}`;
                    return (
                      <article
                        className="invoice-register-row invoice-clickable-row"
                        key={rowMenuKey}
                        onClick={(event) => openInvoiceDetails(r, event)}
                        onKeyDown={(event) => handleInvoiceRowKeyDown(r, event)}
                        tabIndex={0}
                        role="button"
                        aria-label={`View invoice ${r.invoiceNumber || ""}`}
                      >
                        <span className="invoice-number draft-number-cell">
                        <DraftCheckbox bulk={draftBulk} id={r._id} label={r.invoiceNumber} />
                        <button
                          type="button"
                          className="draft-number-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(getInvoiceOpenPath(r));
                          }}
                        >
                          {r.invoiceNumber || "—"}
                        </button>
                        </span>
                        <span className="invoice-customer">
                          {getContactOpenPath(r) ? (
                            <Link
                              className="invoice-contact-link"
                              to={getContactOpenPath(r)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r._customer}
                            </Link>
                          ) : (
                            r._customer
                          )}
                          {r.quotationNumber && (
                            <small>Reference: {r.quotationNumber}</small>
                          )}
                        </span>
                        <span className="invoice-date">
                          {date(r.invoiceDate || r.createdDate)}
                        </span>
                        <span className="invoice-due">
                          {r._key === "draft" ? "Planned " : ""}
                          {date(r.dueDate)}
                        </span>
                        <span className="invoice-lifecycle-status">
                          <i className={statusClassName(r._activityStatus)}>
                            {r._activityStatus}
                          </i>
                        </span>
                        <span className="invoice-status">
                          <i className={r._key}>{paymentStatusLabel(r)}</i>
                          {r._key === "overdue" && (
                            <small>Payment overdue</small>
                          )}
                        </span>
                        <strong className="invoice-total">
                          {money(r._total)}
                        </strong>
                        <span className="invoice-balance">
                          <small>Outstanding balance</small>
                          <strong>
                            {r._key === "draft"
                              ? "— Not issued"
                              : money(r._balance)}
                          </strong>
                        </span>
                        <div
                          className="invoice-row-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="listing-action-trigger"
                            aria-label={`Actions for ${r.invoiceNumber}`}
                            aria-expanded={menu === rowMenuKey}
                            onClick={(event) => {
                              const isOpen = menu === rowMenuKey;
                              setMenu(isOpen ? null : rowMenuKey);
                              setMenuAnchorEl(
                                isOpen ? null : event.currentTarget
                              );
                            }}
                          >
                            <LuEllipsis />
                          </button>
                          {menu === rowMenuKey && (
                            <ListingActionMenu
                              anchorEl={menuAnchorEl}
                              label={`Actions for ${r.invoiceNumber}`}
                              onClose={() => {
                                setMenu(null);
                                setMenuAnchorEl(null);
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setMenu(null);
                                  navigate(getInvoiceOpenPath(r));
                                }}
                              >
                                <LuFileText />
                                View
                              </button>
                              <button
                                type="button"
                                disabled={r._key !== "draft"}
                                title={
                                  r._key !== "draft"
                                    ? "Only draft invoices can be edited"
                                    : "Edit draft invoice"
                                }
                                onClick={() => {
                                  setMenu(null);
                                  if (r._key === "draft") {
                                    navigate(`/invoices/edit/${r._id}`);
                                    return;
                                  }
                                  setEditBlockedRow(r);
                                }}
                              >
                                <LuPencil />
                                Edit
                              </button>
                              {r._key !== "paid" && (
                                <button
                                  type="button"
                                  onClick={() => openEmail(r)}
                                >
                                  <LuMail />
                                  {r.emailSent > 0
                                    ? "Resend email"
                                    : "Send email"}
                                </button>
                              )}
                              {r.invoiceType === "Direct" && (
                                <button type="button" disabled={cloneLoading} onClick={() => clone(r)}>
                                  <LuCopy />
                                  Clone
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setMenu(null);
                                  navigate(
                                    `/invoices/edit/${r._id}?mode=preview`,
                                    {
                                      state: {
                                        previewReturn: {
                                          path: `${window.location.pathname}${
                                            window.location.search
                                          }`,
                                          label: "Back to Invoices",
                                        },
                                      },
                                    }
                                  );
                                }}
                              >
                                <LuEye />
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setMenu(null);
                                  downloadInvoice(
                                    { _id: r._id },
                                    (response) =>
                                      savePdfResponse(
                                        response,
                                        `${r.invoiceNumber || "invoice"}.pdf`
                                      ),
                                    dispatch
                                  );
                                }}
                              >
                                <LuDownload />
                                Download PDF
                              </button>
                              <button
                                type="button"
                                className="danger"
                                disabled={
                                  String(r.activityStatus || "")
                                    .trim()
                                    .toLowerCase() === "sent" ||
                                  ["paid", "voided"].includes(r._key)
                                }
                                title={
                                  String(r.activityStatus || "")
                                    .trim()
                                    .toLowerCase() === "sent"
                                    ? "Sent invoices cannot be deleted"
                                    : ["paid", "voided"].includes(r._key)
                                    ? `${statusLabel(
                                        r._key
                                      )} invoices cannot be deleted`
                                    : "Move invoice to Voided"
                                }
                                onClick={() => {
                                  setDeleteId(r._id);
                                  setDeleteNumber(
                                    r.invoiceNumber || "This invoice"
                                  );
                                  setDeleteError("");
                                  setDeleteOpen(true);
                                  setMenu(null);
                                }}
                              >
                                <LuTrash2 />
                                Delete
                              </button>
                            </ListingActionMenu>
                          )}
                        </div>
                      </article>
                    );
                  })}
              {!listLoading && !visible.length && (
                <div className="invoice-empty">
                  <LuFileText />
                  <strong>
                    {data.length
                      ? "No invoices match these filters"
                      : "No invoices yet"}
                  </strong>
                  <span>
                    {data.length
                      ? "Try changing or clearing the current filters."
                      : "Create your first invoice to start tracking receivables."}
                  </span>
                  {filtersActive ? (
                    showClearFilters && (
                      <button type="button" onClick={clearFilters}>
                        Clear filters
                      </button>
                    )
                  ) : (
                    <button
                      type="button"
                      disabled={!access}
                      onClick={handleOpenNewInvoice}
                    >
                      <LuPlus />
                      New invoice
                    </button>
                  )}
                </div>
              )}
            </div>
            <footer>
              <span>
                Showing {filtered.length ? (page - 1) * SIZE + 1 : 0}–
                {Math.min(page * SIZE, filtered.length)} of {filtered.length}{" "}
                invoices
              </span>
              <div>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <LuChevronLeft />
                </button>
                <button
                  disabled={page === pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <LuChevronRight />
                </button>
              </div>
            </footer>
          </section>
            </>
          )}
        </main>
      </div>
      <DeleteConfirmationDialog
        open={deleteOpen}
        entityName="invoice"
        title="Delete invoice"
        message={
          <>
            <strong>{deleteNumber || "This invoice"}</strong> will remain
            available in the Voided section. This action cannot be undone.
          </>
        }
        confirmLabel="Delete invoice"
        loadingLabel="Deleting invoice…"
        loading={deleteLoading}
        error={deleteError}
        handleDelete={remove}
        handleCancel={() => setDeleteOpen(false)}
        closeModalClick={() => setDeleteOpen(false)}
      />
      <DeleteConfirmationDialog
        open={Boolean(editBlockedRow)}
        title="Invoice cannot be edited"
        message={
          <>
            <strong>{editBlockedRow?.invoiceNumber || "This invoice"}</strong>{" "}
            has already been sent and is locked to preserve its financial
            history. Create a credit note or clone the invoice if changes are
            required.
          </>
        }
        cancelLabel="Close"
        confirmLabel="View invoice"
        closeModalClick={() => setEditBlockedRow(null)}
        handleCancel={() => setEditBlockedRow(null)}
        handleDelete={() => {
          const invoiceID = editBlockedRow?._id;
          setEditBlockedRow(null);
          if (invoiceID) navigate(`/invoices/view/${invoiceID}`);
        }}
      />
      {emailOpen && (
        <SendInvoiceModal
          open={emailOpen}
          onClose={() => {
            setEmailOpen(false);
            setSelectedRowForEmail(null);
          }}
          invoiceNumber={selectedRowForEmail?.invoiceNumber}
          customerName={selectedRowForEmail?._customer || "Customer"}
          customerEmail={
            selectedRowForEmail?.customerEmailAddress ||
            selectedRowForEmail?.emailAddress ||
            ""
          }
          customerEmails={template?.customerEmailAddress}
          invoiceDate={selectedRowForEmail?.invoiceDate}
          dueDate={selectedRowForEmail?.dueDate}
          currency={currency}
          finalAmount={
            selectedRowForEmail?._total || selectedRowForEmail?.finalAmount || 0
          }
          organisation={organisation}
          invoiceStatus={
            selectedRowForEmail?.invoiceStatus ||
            (selectedRowForEmail?._key === "draft" ? "Saved draft" : "Issued")
          }
          templateData={template}
          onSend={sendEmail}
          onViewInvoice={() => {
            setEmailOpen(false);
            if (selectedRowForEmail?._id)
              navigate(`/invoices/view/${selectedRowForEmail._id}`);
          }}
          darkMode={darkMode}
          mode={selectedRowForEmail?._key === "draft" ? "send" : "resend"}
        />
      )}
      <CreateInvoiceModal
        open={createInvoiceModalOpen}
        onClose={() => setCreateInvoiceModalOpen(false)}
        onSelectDirect={() => {
          setCreateInvoiceModalOpen(false);
          navigate("/invoices/add?type=new");
        }}
        onSelectQuotation={(quoteId, quote) => {
          setCreateInvoiceModalOpen(false);
          navigate(`/invoices/add?quotationID=${quoteId}`, {
            state: {
              quotationID: quoteId,
              quotationNumber: quote?.quotationNumber,
              quotation: quote,
            },
          });
        }}
        businessUserID={login.businessUserID}
        darkMode={darkMode}
      />
      {bankingGateOpen && (
        <div className="invoice-bank-gate-backdrop" role="presentation">
          <section
            className="invoice-bank-gate-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-bank-gate-title"
            aria-describedby="invoice-bank-gate-copy"
          >
            <header>
              <LuLandmark aria-hidden="true" />
              <h2 id="invoice-bank-gate-title">
                Add bank details to create an invoice
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setBankingGateOpen(false)}
              >
                <LuX />
              </button>
            </header>
            <div className="invoice-bank-gate-copy">
              <p id="invoice-bank-gate-copy">
                Add your bank details before creating an invoice. This lets you
                receive payments from your customers.
              </p>
            </div>
            <footer>
              <button
                type="button"
                className="invoice-bank-gate-secondary"
                onClick={() => setBankingGateOpen(false)}
              >
                Not now
              </button>
              <button
                type="button"
                className="invoice-bank-gate-primary"
                onClick={() =>
                  navigate(
                    `/settings?tab=payment&edit=bank&returnTo=${encodeURIComponent(
                      "/invoices/add?type=new"
                    )}`
                  )
                }
              >
                Add banking details
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
